import {
  LOOT_FILTER_DEFAULT_MIN_ITEM_LEVEL,
  TRADESKILL_XP_START,
} from '@helpers/config';
import { allEquipmentItemTypes } from '@helpers/item/equipment-types';
import { rngUuid } from '@helpers/rng';
import type {
  GameStateRunStats,
  HeroRunStats,
  AutoModeState,
  CombatStatBlock,
  ElementBlock,
  EquipmentBlock,
  EquipmentItemType,
  GameId,
  GameState,
  GameStateTradeskills,
  GatheringState,
  GlobalEffectSums,
  LootFilterSettings,
  MonsterType,
  StatBlock,
  StatusEffectBlock,
  TradeskillBuildingState,
  TravelState,
  WorkerStatBlock,
} from '@interfaces';

// 2: saves track gather-node visits from creation, so they skip the legacy gather-node backfill.
export const GAMESTATE_VERSION = 3;

export function defaultGameState(): GameState {
  return {
    meta: {
      version: GAMESTATE_VERSION,
      isSetup: false,
      isPaused: false,
      createdAt: Date.now(),
      titleCrawlPending: false,
      outroCrawlSeen: false,
      victoryPopupSeen: false,
    },
    gameId: rngUuid() as GameId,
    clock: {
      numTicks: 0,
      lastSaveTick: 0,
    },
    world: {
      party: [],
      currentLocation: {
        mapName: 'Carrina',
        x: 26,
        y: 24,
      },
      travel: defaultTravelState(),
      gathering: defaultGatheringState(),
      autoMode: defaultAutoModeState(),
      exploreRandom: {},
      caravans: {},
      commissions: {},
      towns: {},
    },
    edicts: [],
    materials: {},
    discoveredMaterials: {},
    collectibles: {},
    armory: [],
    regalias: { entries: [], vault: [] },
    lootFilters: defaultLootFilterSettings(),
    discoveredEquipment: {},
    discoveredCaravans: {},
    discoveredTraders: {},
    discoveredTrainers: {},
    discoveredRecipes: {},
    discoveredGatherNodes: {},
    gatherNodeLevels: {},
    shrines: {},
    outposts: {},
    worldDiscoveries: {},
    discoveredMaps: {},
    bestiary: {},
    workers: {},
    discoveredWorkers: {},
    globalEffects: [],
    globalEffectSums: defaultGlobalEffectSums(),
    tradeskills: defaultTradeskills(),
    discoveredAstralProjectorSpells: {},
    activeAstralProjectorSpells: [],
    tutorials: {},
    regionIntros: {},
    tasks: {},
    runStats: defaultRunStats(),
  };
}

export function defaultHeroRunStats(): HeroRunStats {
  return {
    kills: 0,
    damageDealt: 0,
    damageTaken: 0,
    deaths: 0,
    healing: 0,
    crits: 0,
    statusEffectsApplied: 0,
    damageBlocked: 0,
    fights: 0,
    skillUses: {},
  };
}

export function defaultRunStats(): GameStateRunStats {
  return {
    trackedFromTick: 0,
    goldEarned: 0,
    fightsWon: 0,
    fightsLost: 0,
    itemsCrafted: 0,
    heroes: {},
  };
}

// Deliberately empty and content-free - `defaultGameState()` runs at
// module-eval time, before content is loaded. Real entries are populated
// once content is guaranteed loaded.
function defaultTradeskills(): GameStateTradeskills {
  return {} as GameStateTradeskills;
}

export function defaultTradeskillBuilding(): TradeskillBuildingState {
  return {
    level: 1,
    xp: { current: 0, maximum: TRADESKILL_XP_START },
    queue: [],
  };
}

export function defaultTravelState(): TravelState {
  return {
    status: 'Idle',
    path: [],
    ticksIntoStep: 0,
  };
}

export function defaultGatheringState(): GatheringState {
  return {
    status: 'Idle',
    ticksIntoGather: 0,
  };
}

export function defaultLootFilterSettings(): LootFilterSettings {
  return {
    sellFromSources: {
      Combat: true,
      Crafting: true,
    },
    keepRarities: {
      Common: true,
      Uncommon: true,
      Rare: true,
      Mystical: true,
      Legendary: true,
    },
    minimumItemLevel: LOOT_FILTER_DEFAULT_MIN_ITEM_LEVEL,
    keepEquipmentTypes: Object.fromEntries(
      allEquipmentItemTypes().map((type) => [type, true]),
    ) as Record<EquipmentItemType, boolean>,
  };
}

export function defaultAutoModeState(): AutoModeState {
  return {
    enabled: false,
    clauses: [],
    waitForFullHealthBeforeCombat: false,
    waitForFullEnergyBeforeCombat: false,
    nodeFailureCounts: {},
  };
}

export function defaultStats(): StatBlock {
  return {
    Agility: 0,
    Energy: 0,
    Health: 0,
    Intelligence: 0,
    Luck: 0,
    Resistance: 0,
    Strength: 0,
    Vitality: 0,
    Spirit: 0,
    Constitution: 0,
  };
}

export function defaultWorkerStats(): WorkerStatBlock {
  return {
    capacity: 0,
    gatherSpeed: 0,
    craftSpeed: 0,
    stamina: 0,
  };
}

export function defaultAffinities(): ElementBlock {
  return {
    Fire: 0,
    Water: 0,
    Earth: 0,
    Air: 0,
  };
}

export function defaultCombatStats(): CombatStatBlock {
  return {
    repeatActionChance: 0,
    skillStrikeAgainChance: 0,
    redirectionChance: 0,
    missChance: 0,
    debuffIgnoreChance: 0,
    damageReflectPercent: 0,
    healingIgnorePercent: 0,
    reviveChance: 0,
    stunChance: 0,
    epCostIncreasePercent: 0,
    agroValue: 0,
    coverThreshold: 0,
  };
}

export function defaultTagResistances(): StatusEffectBlock {
  return {
    Stun: 0,
    StatDown: 0,
    Accuracy: 0,
    DamageOverTime: 0,
    Poison: 0,
    Burn: 0,
    Bleed: 0,
  };
}

export function defaultGlobalEffectSums(): GlobalEffectSums {
  return {
    stats: defaultStats(),
    combatStats: defaultCombatStats(),
    debuffResistanceTags: defaultTagResistances(),
    debuffResistanceFlat: 0,
    xpGainMultiplierBonus: 0,
    goldGainMultiplierBonus: 0,
    combatItemDropRateBoost: 0,
    gatheringItemDropRateBoost: 0,
    armorySizeBoost: 0,
    tradeskillQueueSizeBoosts: {},
    offPathTravelSpeedBonus: 0,
    onPathTravelSpeedBonus: 0,
    decreeClauseCapBoost: 0,
  };
}

export function defaultMonsterTypeDamageBonus(): Record<MonsterType, number> {
  return {
    Humanoid: 0,
    Demon: 0,
    Amalgamation: 0,
    Insect: 0,
    Beast: 0,
    Spirit: 0,
    Forgetter: 0,
  };
}

export function defaultEquipment(): EquipmentBlock {
  return {
    Armor: undefined,
    Helmet: undefined,
    Weapon: undefined,
    Offhand: undefined,
    Ring: undefined,
    Accessory: undefined,
    Artifact: undefined,
    Ammo: undefined,
  };
}
